import { ChevronDown, Minimize } from "lucide-react";

import { cn } from "../lib/utils";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";

/**
 * What a host reports about a compaction pass. Structural on purpose: the host
 * owns the compaction algorithm, the shell only reports it happened and by how
 * much. A host whose compactor tracks fewer numbers leaves them off.
 */
export interface CompactionMetadata {
  /** Which tier fired: 0 = no-op, 1 = tool clearing, 2 = summarization. */
  tier: number;
  originalTokenEstimate: number;
  compactedTokenEstimate: number;
  /** Tool outputs dropped, when the tier clears them. */
  toolResultsCleared?: number;
}

/**
 * The transcript's marker that older context was dropped or summarized.
 *
 * It renders in the message list, not as chrome, because that is where it
 * happened: everything above it is a compressed account of the conversation and
 * everything below is verbatim. The disclosure holds the summary the host
 * produced.
 */
export function CompactionIndicator({
  summary,
  metadata,
  className,
}: {
  summary: string;
  metadata: CompactionMetadata;
  className?: string;
}) {
  const cleared = metadata.toolResultsCleared ?? 0;
  return (
    <Collapsible
      className={cn(
        "group my-1 w-full rounded-md border border-dashed border-muted-foreground/30 text-xs",
        className,
      )}
    >
      <CollapsibleTrigger className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted/40">
        <Minimize className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="font-medium text-muted-foreground">
          Context compacted
        </span>
        <span className="rounded bg-muted px-1 text-[10px] tabular-nums text-muted-foreground">
          ~{tokensK(metadata.originalTokenEstimate)}K → ~
          {tokensK(metadata.compactedTokenEstimate)}K tokens
        </span>
        {cleared > 0 ? (
          <span className="text-[11px] text-muted-foreground">
            {cleared === 1
              ? "1 tool result cleared"
              : `${cleared} tool results cleared`}
          </span>
        ) : null}
        <ChevronDown className="ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t border-dashed border-muted-foreground/30 px-3 py-2 text-muted-foreground">
        {summary}
      </CollapsibleContent>
    </Collapsible>
  );
}

function tokensK(tokens: number): number {
  return Math.round(tokens / 1000);
}
